import { useContext, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import type { IntakeState } from "./portal-utils";
import { StepCoverContext } from "./step-cover-context";
import { coverStyle } from "@/pages/raise-board/rb-ui";

/*
 * Primitives local to the fund portal and the submission flow. Everything here
 * sits inside the public theme (see RaiseBoardShell / .public-theme) and only
 * uses its tokens.
 */

/**
 * A section of the portal page: an ink rule across the top, the title set
 * large in a left column, the content beside it. The rule and the column are
 * what give the page its deal-room structure without boxing anything in.
 */
export function PortalSection({
  title,
  children,
  className,
  id,
}: {
  title: ReactNode;
  children: ReactNode;
  className?: string;
  id?: string;
}) {
  return (
    <section
      id={id}
      className={cn(
        "grid gap-x-12 gap-y-6 border-t border-foreground pt-7 motion-safe:animate-rise lg:grid-cols-[13rem_minmax(0,1fr)]",
        className
      )}
    >
      <h2 className="text-[clamp(1.35rem,1.15rem+0.8vw,1.75rem)] font-semibold leading-tight tracking-[-0.03em]">
        {title}
      </h2>
      <div className="min-w-0">{children}</div>
    </section>
  );
}

/** A definition list whose rows put the term on the left and the value beside it. */
export function DefList({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <dl className={cn("divide-y divide-border/70", className)}>{children}</dl>
  );
}

export function DefRow({
  term,
  children,
}: {
  term: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="grid gap-x-8 gap-y-2 py-4 first:pt-0 last:pb-0 sm:grid-cols-[168px_minmax(0,1fr)]">
      <dt className="pt-0.5 text-[14px] text-muted-foreground">{term}</dt>
      <dd className="min-w-0 text-[15px]">{children}</dd>
    </div>
  );
}

/** A dot and a few words saying whether the portal is taking applications. */
export function IntakeStatus({
  state,
  className,
  tone = "paper",
}: {
  state: IntakeState;
  className?: string;
  /** On an ink cover the green has to be lighter to read. */
  tone?: "paper" | "ink";
}) {
  const label =
    state === "open"
      ? "Applications open"
      : state === "upcoming"
        ? "Opening soon"
        : state === "closed"
          ? "Applications closed"
          : "Accepting applications";
  const live = state === "open" || state === "rolling";
  const liveText = tone === "ink" ? "text-[hsl(156_58%_62%)]" : "text-raising";
  const liveDot = tone === "ink" ? "bg-[hsl(156_58%_62%)]" : "bg-raising";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 text-[13px] font-medium",
        live ? liveText : "text-muted-foreground",
        className
      )}
    >
      <span className="relative flex h-1.5 w-1.5 shrink-0">
        {live && (
          <span
            className={cn(
              "absolute inset-0 rounded-full opacity-40 motion-safe:animate-ping [animation-duration:2.4s]",
              liveDot
            )}
          />
        )}
        <span
          className={cn(
            "relative h-1.5 w-1.5 rounded-full",
            live ? liveDot : "bg-muted-foreground/50"
          )}
        />
      </span>
      {label}
    </span>
  );
}

/**
 * Form field frame for the submission flow: label, control, then help or
 * error text in the same place every time.
 */
export function Field({
  label,
  htmlFor,
  required,
  help,
  error,
  children,
  className,
}: {
  label: ReactNode;
  htmlFor?: string;
  required?: boolean;
  help?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("space-y-2", className)}>
      <label htmlFor={htmlFor} className="block text-[14px] font-medium text-foreground">
        {label}
        {required && (
          <span aria-hidden className="ml-0.5 text-muted-foreground">
            *
          </span>
        )}
      </label>
      {children}
      {error ? (
        <p role="alert" className="text-[13px] text-destructive">
          {error}
        </p>
      ) : help ? (
        <p className="text-[13px] leading-relaxed text-muted-foreground">{help}</p>
      ) : null}
    </div>
  );
}

/** A neutral bordered note: guidance, requirements, quiet confirmations. */
export function Note({
  icon,
  title,
  children,
  className,
}: {
  icon?: ReactNode;
  title?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex gap-3 rounded-lg border border-border bg-card px-4 py-3.5 text-[14px] leading-relaxed",
        className
      )}
    >
      {icon && (
        <span className="mt-0.5 shrink-0 text-muted-foreground [&_svg]:h-4 [&_svg]:w-4">
          {icon}
        </span>
      )}
      <div className="min-w-0">
        {title && <p className="font-medium text-foreground">{title}</p>}
        {children && (
          <div className={cn("text-muted-foreground", title && "mt-0.5")}>{children}</div>
        )}
      </div>
    </div>
  );
}

/*
 * Step frame for the submission flow. Drop-in replacements for the shadcn
 * Card parts the steps were written against: on the public theme a step is
 * not a box on a background, it is the page. Title, one line of context,
 * then the form, in a single readable column.
 */
export function StepCard({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn("motion-safe:animate-rise", className)}>{children}</div>;
}

/**
 * The step's ink band: full-bleed from inside the step's column, carrying
 * the "Step N of 4" line from StepCoverContext, the step's title and its one
 * line of context. The form follows on paper.
 *
 * Full-bleed is done by centring a 100vw box on the column; the submission
 * page clips horizontal overflow so a classic scrollbar can't cause a sideways
 * scroll.
 */
export function StepCardHeader({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const cover = useContext(StepCoverContext);
  return (
    <div
      className={cn(
        "ink-scope grain relative left-1/2 isolate mb-12 w-screen -translate-x-1/2 overflow-hidden bg-background [--grain-opacity:0.09]",
        className
      )}
      style={coverStyle(cover?.background)}
    >
      <div className="mx-auto w-full max-w-2xl px-5 pb-11 pt-10 sm:px-8 sm:pb-14 sm:pt-14">
        {(cover?.eyebrow || cover?.aside) && (
          <div className="mb-6 flex min-h-5 items-center justify-between gap-4">
            <div className="text-[12px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              {cover?.eyebrow}
            </div>
            {cover?.aside}
          </div>
        )}
        <div className="space-y-3 motion-safe:animate-rise">{children}</div>
      </div>
    </div>
  );
}

export function StepCardTitle({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <h1
      className={cn(
        "text-balance text-[clamp(2.1rem,1.4rem+2.6vw,3.25rem)] font-semibold leading-[1.02] tracking-[-0.04em] text-foreground",
        className
      )}
    >
      {children}
    </h1>
  );
}

export function StepCardDescription({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "max-w-[56ch] pt-1 text-[16px] leading-relaxed text-muted-foreground sm:text-[17px]",
        className
      )}
    >
      {children}
    </div>
  );
}

export function StepCardContent({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  // One field vocabulary for every step: text controls sit on the card
  // surface at a single height, whatever component the step happened to use.
  return (
    <div
      className={cn(
        "space-y-7 [&_input:not([type=checkbox]):not([type=radio]):not([type=file])]:h-10 [&_input:not([type=checkbox]):not([type=radio])]:bg-card [&_select]:h-10 [&_select]:bg-card [&_textarea]:bg-card [&_button[role=combobox]]:min-h-10 [&_button[role=combobox]]:bg-card",
        className
      )}
    >
      {children}
    </div>
  );
}

/** The column every step sits in. */
export function StepColumn({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("mx-auto w-full max-w-2xl px-5 sm:px-8", className)}>
      {children}
    </div>
  );
}
